import React, { useEffect, useMemo, useState } from 'react';
import { PromptVariable } from '../types';
import {
  defaultVariableValues,
  extractVariables,
  substituteVariables,
} from '../utils/variables';
import CopyButton from './CopyButton';

interface VariableSubstitutionPanelProps {
  systemText?: string;
  userText: string;
  variables?: PromptVariable[];
}

/**
 * Interactive panel: set variable values, live-preview substituted prompts, copy result.
 */
export const VariableSubstitutionPanel: React.FC<VariableSubstitutionPanelProps> = ({
  systemText,
  userText,
  variables = [],
}) => {
  const detectedNames = useMemo(() => {
    const fromText = [
      ...extractVariables(systemText || ''),
      ...extractVariables(userText || ''),
    ];
    const fromMeta = variables.map((v) => v.name);
    return Array.from(new Set([...fromMeta, ...fromText]));
  }, [systemText, userText, variables]);

  const [values, setValues] = useState<Record<string, string>>(() =>
    defaultVariableValues(variables)
  );

  useEffect(() => {
    setValues((prev) => {
      const defaults = defaultVariableValues(variables);
      const next: Record<string, string> = { ...defaults };
      for (const name of detectedNames) {
        if (prev[name] !== undefined && prev[name] !== '') {
          next[name] = prev[name];
        } else if (next[name] === undefined) {
          next[name] = '';
        }
      }
      return next;
    });
  }, [detectedNames, variables]);

  if (detectedNames.length === 0) {
    return null;
  }

  const substitutedSystem = systemText
    ? substituteVariables(systemText, values)
    : undefined;
  const substitutedUser = substituteVariables(userText, values);

  const metaByName = Object.fromEntries(variables.map((v) => [v.name, v]));

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-lg font-semibold text-gray-900">Variable Values</h3>
        <p className="text-sm text-gray-500">
          Fill values for a live preview of the substituted prompt.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {detectedNames.map((name) => {
          const meta = metaByName[name];
          const isUndefined = !meta;
          return (
            <div key={name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="font-mono text-blue-700">{`\${${name}}`}</span>
                {meta?.required && (
                  <span className="text-red-500 ml-1" title="Required">
                    *
                  </span>
                )}
                {isUndefined && (
                  <span className="ml-2 text-xs text-amber-700 font-normal">
                    not in metadata
                  </span>
                )}
              </label>
              {meta?.type === 'enum' && meta.options && meta.options.length > 0 ? (
                <select
                  value={values[name] ?? ''}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [name]: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                >
                  <option value="">Select…</option>
                  {meta.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : meta?.type === 'boolean' ? (
                <select
                  value={values[name] ?? ''}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [name]: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                >
                  <option value="">Select…</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input
                  type={
                    meta?.type === 'number' || meta?.type === 'range'
                      ? 'number'
                      : 'text'
                  }
                  min={meta?.min}
                  max={meta?.max}
                  value={values[name] ?? ''}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [name]: e.target.value }))
                  }
                  placeholder={meta?.description || `Value for ${name}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Preview</h4>
          <CopyButton
            text={[substitutedSystem, substitutedUser].filter(Boolean).join('\n\n')}
            label="Copy Preview"
          />
        </div>

        {substitutedSystem !== undefined && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-500 uppercase">
                System
              </span>
              <CopyButton text={substitutedSystem} label="Copy" className="scale-90 origin-right" />
            </div>
            <pre className="bg-white border border-gray-200 p-3 rounded-md text-sm overflow-x-auto whitespace-pre-wrap">
              {substitutedSystem}
            </pre>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-500 uppercase">User</span>
            <CopyButton text={substitutedUser} label="Copy" className="scale-90 origin-right" />
          </div>
          <pre className="bg-white border border-gray-200 p-3 rounded-md text-sm overflow-x-auto whitespace-pre-wrap">
            {substitutedUser}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default VariableSubstitutionPanel;
